import type { EntitySchema, FieldSchema } from "./fieldSchema";

export type ModalFieldConfig<T, K extends keyof T = keyof T> = {
  key: K;
  label: string;
  render?: (value: T[K], row: T) => React.ReactNode;
};

export type ColumnConfig<T, K extends keyof T = keyof T> = {
  header: string;
  accessor: K;
  sortable?: boolean;
  sortField?: string;
  render?: (value: T[K], row: T) => React.ReactNode;
};

/**
 * Build column configs from entity schema
 */
export function buildColumnsFromSchema<T>(
  schema: EntitySchema<T>,
  fieldsToInclude?: (keyof T)[],
): ColumnConfig<T>[] {
  const keys = fieldsToInclude || (Object.keys(schema) as (keyof T)[]);

  return keys.map(key => {
    const field = schema[key] as FieldSchema<T[typeof key], T>;

    return {
      header: field.label,
      accessor: key,
      sortable: field.sortable,
      sortField: field.sortField,
      render: field.render,
    } as ColumnConfig<T>;
  });
}

/**
 * Build modal field configs from entity schema
 */
export function buildModalFieldsFromSchema<T>(
  schema: EntitySchema<T>,
  fieldsToInclude?: (keyof T)[],
): ModalFieldConfig<T>[] {
  const keys = fieldsToInclude || (Object.keys(schema) as (keyof T)[]);

  return keys.map(key => {
    const field = schema[key] as FieldSchema<T[typeof key], T>;

    return {
      key,
      label: field.label,
      render: field.render,
    } as ModalFieldConfig<T>;
  });
}
